import {createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useForm, UseFormReturn} from "react-hook-form";
import {v4 as uuidv4} from "uuid";
import {useSearchEtaNotificationConfigsQuery, useSetEtaNotificationConfigMutation} from "../../../../generated/graphql";
import {OptionType} from "../../../common/types";
import {showToastWithTitle} from "../../../../utils/toasters/ToasterWithTitle";
import log from "loglevel";
import {useFeatureFlagContext} from "../../../../providers/FeatureFlagProvider";
import {getConfig} from "../../../../services/TenantService";
import {Constants} from "../../../../constants/constants";
import {useOktaAuth} from "@okta/okta-react";

const LOG_PREFIX = "[ETA Config Provider]";
const SITE_ID = 0;
const QUERY_LIMIT = 1000;

type FormMode = "create" | "edit" | null;
type TriggerType = "eta" | "stop";

enum AttributeKeys {
  SERVICE_TYPES = "serviceTypes",
  STOP_TYPES = "stopTypes",
  CUSTOMERS = "customers",
  CUSTOMER_GROUPS = "customerGroups",
  VEHICLE_TYPES = "vehicleTypes",
  PRIORITIES = "priorities",
  SITES = "sites",
  DRIVERS = "drivers",
  DRIVER_GROUPS = "dispatchGroups"
}

export enum SendNotificationType {
  EMAIL = "email",
  TEXT = "text"
}

export enum SendNotificationOption {
  STOP = "stop",
  CALLER = "caller",
  CUSTOM = "custom"
}

export interface NotificationConfig {
  triggerWhen: TriggerType;
  awayFrom: string;
  timeWindowBeforeETA: string;
  timeWindowAfterETA: string;
}

export interface SecondNotificationConfig extends NotificationConfig {
  enabled: boolean;
}

export interface OptimizationNotification {
  enabled: boolean;
  timeWindowBeforeETA: string;
  timeWindowAfterETA: string;
}

export interface ETANotificationConfigsFormValues {
  id?: string;
  rank?: number;
  name: string;
  attributes: Record<AttributeKeys, OptionType[]>;
  sendNotificationVia: {
    type: SendNotificationType;
    value: string;
    option: SendNotificationOption;
  };
  optimizationNotification: OptimizationNotification;
  firstNotification: NotificationConfig;
  secondNotification: SecondNotificationConfig;
  isFollowedUp: boolean;
}

interface EvaluationState {
  loading: boolean;
  error: string | null;
  data: ETANotificationConfigsFormValues[];
}

interface ETANotificationContextState {
  isLoading: boolean;
  includeOptimizationETAEnabled: boolean;
  etaFormState: UseFormReturn<ETANotificationConfigsFormValues>;
  evaluationState: EvaluationState;
  formMode: FormMode;
  isFormValid: boolean;
  isDirty: boolean;
  onCreateNew: () => void;
  onEdit: (config: ETANotificationConfigsFormValues) => void;
  onCancel: () => void;
  onSave: () => Promise<void>;
  onDelete: () => Promise<void>;
  onDragEnd: (result: any) => Promise<void>;
}

interface ETANotificationConfigsProviderProps {
  children: ReactNode;
}

const ETANotificationContext = createContext<ETANotificationContextState | null>(null);

export const createInitialNotificationConfig = (): NotificationConfig => ({
  triggerWhen: "eta",
  awayFrom: "",
  timeWindowBeforeETA: "",
  timeWindowAfterETA: ""
});

export const createInitialSecondNotificationConfig = (): SecondNotificationConfig => ({
  ...createInitialNotificationConfig(),
  enabled: false
});

export const createInitialOptimizationNotification = (): OptimizationNotification => ({
  enabled: false,
  timeWindowBeforeETA: "",
  timeWindowAfterETA: ""
});

const INITIAL_FORM_VALUES: ETANotificationConfigsFormValues = {
  name: "",
  attributes: {
    [AttributeKeys.SERVICE_TYPES]: [],
    [AttributeKeys.STOP_TYPES]: [],
    [AttributeKeys.CUSTOMERS]: [],
    [AttributeKeys.CUSTOMER_GROUPS]: [],
    [AttributeKeys.VEHICLE_TYPES]: [],
    [AttributeKeys.PRIORITIES]: [],
    [AttributeKeys.SITES]: [],
    [AttributeKeys.DRIVERS]: [],
    [AttributeKeys.DRIVER_GROUPS]: []
  },
  sendNotificationVia: {
    type: SendNotificationType.EMAIL,
    value: "",
    option: SendNotificationOption.STOP
  },
  optimizationNotification: createInitialOptimizationNotification(),
  firstNotification: createInitialNotificationConfig(),
  secondNotification: createInitialSecondNotificationConfig(),
  isFollowedUp: false
};

const parseConfigJSON = (configString: string): ETANotificationConfigsFormValues[] | null => {
  try {
    const parsed: unknown = JSON.parse(configString);

    if (!Array.isArray(parsed)) {
      log.warn(`${LOG_PREFIX} Parsed data is not an array:`, parsed);
      return null;
    }

    log.info(`${LOG_PREFIX} Successfully parsed config:`, parsed);
    return (parsed as ETANotificationConfigsFormValues[]).map((config) => ({
      ...config,
      attributes: {
        ...INITIAL_FORM_VALUES.attributes,
        ...config.attributes
      }
    }));
  } catch (error) {
    log.error(`${LOG_PREFIX} Failed to parse config JSON:`, error);
    return null;
  }
};

const createEmptyEvaluationState = (error: string | null = null): EvaluationState => ({
  loading: false,
  error,
  data: []
});

export const ETANotificationConfigsProvider: FC<ETANotificationConfigsProviderProps> = ({children}) => {
  const {oktaAuth} = useOktaAuth();

  const [optimizationConfigLoading, setOptimizationConfigLoading] = useState(false);
  const [isMobileOptimizationEnabled, setIsMobileOptimizationEnabled] = useState(false);

  const {manifestOptimization: isManifestOptimizationEnabled} = useFeatureFlagContext();

  const [formMode, setFormMode] = useState<FormMode>(null);
  const [evaluationState, setEvaluationState] = useState<EvaluationState>({
    loading: true,
    error: null,
    data: []
  });

  const sortKeyRef = useRef<string | null>(null);

  const formState = useForm<ETANotificationConfigsFormValues>({
    defaultValues: INITIAL_FORM_VALUES,
    mode: "onChange"
  });

  const isFormValid = formState.formState.isValid;
  const isDirty = formState.formState.isDirty;
  const [mutateConfig] = useSetEtaNotificationConfigMutation();

  useEffect(() => {
    const token = oktaAuth.getAccessToken();
    if (!token) {
      return;
    }
    setOptimizationConfigLoading(true);
    getConfig(token, Constants.MOBILE_ETA_NOTIFICATION_CONFIG_KEY)
      .then((res) => {
        if (res.data.items.length > 0) {
          const isExistEnabledConfig = res.data.items.some((item) => item.configValue === "1");
          setIsMobileOptimizationEnabled(isExistEnabledConfig);
        }
      })
      .catch(() => {
        setOptimizationConfigLoading(false);
      });
  }, [oktaAuth]);

  const {loading: configLoading} = useSearchEtaNotificationConfigsQuery({
    variables: {
      filter: {
        siteId: {eq: SITE_ID}
      },
      limit: QUERY_LIMIT
    },
    onCompleted: (data) => {
      log.info(`${LOG_PREFIX} Raw API response:`, data);

      const items = data?.searchEtaNotificationConfigs?.items ?? [];
      log.info(`${LOG_PREFIX} Total items fetched: ${items.length}`);

      if (items.length === 0 || !items[0]) {
        setEvaluationState(createEmptyEvaluationState());
        log.info(`${LOG_PREFIX} No existing configurations found`);
        return;
      }

      const firstItem = items[0];
      sortKeyRef.current = firstItem.sk ?? null;

      if (!firstItem.config) {
        setEvaluationState(createEmptyEvaluationState());
        log.warn(`${LOG_PREFIX} First item has no config field`);
        return;
      }

      const parsedConfig = parseConfigJSON(firstItem.config);

      if (parsedConfig) {
        updateEvaluationState(parsedConfig);
        log.info(`${LOG_PREFIX} Loaded ${parsedConfig.length} configurations`);
      } else {
        setEvaluationState(createEmptyEvaluationState("Failed to parse configuration data"));
        log.error(`${LOG_PREFIX} Invalid config format in first item`);
      }
    },
    onError: (error) => {
      log.error(`${LOG_PREFIX} Query error:`, error);
      setEvaluationState(
        createEmptyEvaluationState("Something went wrong while fetching ETA Notification Configurations.")
      );
    }
  });

  const includeOptimizationETAEnabled = isManifestOptimizationEnabled || isMobileOptimizationEnabled;

  const isLoading = configLoading || optimizationConfigLoading;

  const updateEvaluationState = useCallback((updatedData: ETANotificationConfigsFormValues[]) => {
    setEvaluationState({
      loading: false,
      error: null,
      data: updatedData
    });
  }, []);

  const saveConfigToServer = useCallback(
    async (configs: ETANotificationConfigsFormValues[]) => {
      const configString = JSON.stringify(configs);

      const result = await mutateConfig({
        variables: {
          input: {
            siteId: SITE_ID,
            config: configString
          },
          ...(sortKeyRef.current && {sk: sortKeyRef.current})
        }
      });

      log.info(`${LOG_PREFIX} Mutation result:`, result);
      return result;
    },
    [mutateConfig]
  );

  const onCreateNew = useCallback(() => {
    log.info(`${LOG_PREFIX} Creating new config`);
    setFormMode("create");
    formState.reset(INITIAL_FORM_VALUES);
  }, [formState]);

  const onEdit = useCallback(
    (config: ETANotificationConfigsFormValues) => {
      log.info(`${LOG_PREFIX} Editing config:`, config);
      setFormMode("edit");
      formState.reset(config);
    },
    [formState]
  );

  const onCancel = useCallback(() => {
    log.info(`${LOG_PREFIX} Canceling config edit`);
    setFormMode(null);
    formState.reset(INITIAL_FORM_VALUES);
  }, [formState]);

  const onSave = useCallback(async () => {
    const isEditMode = formMode === "edit";
    try {
      const formValues = formState.getValues();
      let updatedConfigs: ETANotificationConfigsFormValues[];

      if (isEditMode) {
        updatedConfigs = evaluationState.data.map((config) =>
          config.id === formValues.id
            ? {
                ...formValues,
                rank: config.rank,
                id: config.id
              }
            : config
        );
      } else {
        const newConfig: ETANotificationConfigsFormValues = {
          ...formValues,
          rank: evaluationState.data.length + 1,
          id: uuidv4()
        };
        updatedConfigs = [...evaluationState.data, newConfig];
      }

      await saveConfigToServer(updatedConfigs);
      updateEvaluationState(updatedConfigs);
      setFormMode(null);
      formState.reset(INITIAL_FORM_VALUES);

      const successMessage = isEditMode
        ? "ETA Notification Configuration has been updated!"
        : "New ETA Notification Configuration has been created!";

      log.info(`${LOG_PREFIX} Config ${isEditMode ? "edited" : "saved"} successfully`);

      showToastWithTitle({
        intent: "success",
        title: "Success",
        message: successMessage
      });
    } catch (error) {
      const errorMessage = isEditMode
        ? "Failed to update configuration. Please try again."
        : "Failed to create configuration. Please try again.";

      log.error(`${LOG_PREFIX} Failed to ${isEditMode ? "edit" : "save"} config:`, error);

      showToastWithTitle({
        intent: "danger",
        title: "Error",
        message: errorMessage
      });
    }
  }, [formState, evaluationState.data, saveConfigToServer, updateEvaluationState, formMode]);

  const onDelete = useCallback(async () => {
    try {
      const formValues = formState.getValues();
      log.info(`${LOG_PREFIX} Deleting config:`, formValues);

      const filteredConfigs = evaluationState.data.filter((config) => config.id !== formValues.id);
      const reindexedConfigs = filteredConfigs.map((config, index) => ({
        ...config,
        rank: index + 1
      }));

      await saveConfigToServer(reindexedConfigs);
      updateEvaluationState(reindexedConfigs);
      setFormMode(null);
      formState.reset(INITIAL_FORM_VALUES);
      log.info(`${LOG_PREFIX} Config deleted successfully`);

      showToastWithTitle({
        intent: "success",
        title: "Success",
        message: "ETA Notification Configuration has been deleted!"
      });
    } catch (error) {
      log.error(`${LOG_PREFIX} Failed to delete config:`, error);
      showToastWithTitle({
        intent: "danger",
        title: "Error",
        message: "Failed to delete configuration. Please try again."
      });
    }
  }, [formState, evaluationState.data, saveConfigToServer, updateEvaluationState]);

  const onDragEnd = useCallback(
    async (result: any) => {
      if (!result.destination) {
        log.info(`${LOG_PREFIX} Drag cancelled - no destination`);
        return;
      }

      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;

      if (sourceIndex === destinationIndex) {
        log.info(`${LOG_PREFIX} Drag cancelled - same position`);
        return;
      }

      log.info(`${LOG_PREFIX} Reordering: ${sourceIndex} -> ${destinationIndex}`);

      const reorderedConfigs = Array.from(evaluationState.data);
      const [removed] = reorderedConfigs.splice(sourceIndex, 1);
      reorderedConfigs.splice(destinationIndex, 0, removed);

      const reindexedConfigs = reorderedConfigs.map((config, index) => ({
        ...config,
        rank: index + 1
      }));

      updateEvaluationState(reindexedConfigs);

      try {
        await saveConfigToServer(reindexedConfigs);
        log.info(`${LOG_PREFIX} Reorder saved successfully`);
      } catch (error) {
        log.error(`${LOG_PREFIX} Failed to save reorder:`, error);
        updateEvaluationState(evaluationState.data);
        showToastWithTitle({
          intent: "danger",
          title: "Error",
          message: "Failed to save new order. Please try again."
        });
      }
    },
    [evaluationState.data, saveConfigToServer, updateEvaluationState]
  );

  const contextValue = useMemo<ETANotificationContextState>(
    () => ({
      isLoading,
      includeOptimizationETAEnabled,
      etaFormState: formState,
      evaluationState,
      formMode,
      isFormValid,
      isDirty,
      onCreateNew,
      onEdit,
      onCancel,
      onSave,
      onDelete,
      onDragEnd
    }),
    [
      isLoading,
      includeOptimizationETAEnabled,
      formState,
      evaluationState,
      formMode,
      isFormValid,
      isDirty,
      onCreateNew,
      onEdit,
      onCancel,
      onSave,
      onDelete,
      onDragEnd
    ]
  );

  return <ETANotificationContext.Provider value={contextValue}>{children}</ETANotificationContext.Provider>;
};

export const useETANotificationContext = () => {
  const context = useContext(ETANotificationContext);
  if (!context) {
    throw new Error("useETANotificationContext must be used within ETANotificationConfigsProvider");
  }
  return context;
};
